import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";
import { api, afterLookup, submitAfter } from "@/lib/gfApi";

const PURPLE = "#4B006E";
const GOLD = "#D4AF37";

export default function CohortAfter() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("lookup"); // lookup | quiz | done
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [participant, setParticipant] = useState(null);
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    api.get("/cohort/questions?type=after").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const doLookup = async () => {
    if (!code.trim() || !email.trim()) { toast.error("Sila masukkan kod dan emel."); return; }
    setBusy(true);
    try {
      const p = await afterLookup(code.trim(), email.trim());
      setParticipant(p);
      setStage("quiz");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tidak dapat mengesahkan. Sila cuba lagi.");
    } finally { setBusy(false); }
  };

  const finish = async (final) => {
    setBusy(true);
    try {
      const ans = data.questions.map((qq) => final[qq.id]);
      const res = await submitAfter(participant.participant_id, ans);
      setMilestone(res.milestone);
      setStage("done");
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Gagal menghantar. Sila cuba lagi.");
    }
  };

  /* ---- Lookup ---- */
  if (stage === "lookup") {
    return (
      <GFFrame testId="cohort-after-lookup-page">
        <div className="flex min-h-screen flex-col px-6 pb-10 pt-8" style={{ background: PURPLE }}>
          <button onClick={() => navigate("/cohort")} className="flex w-fit items-center gap-1 text-sm" style={{ color: "#C9B2D6" }} data-testid="after-back-btn">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
            <div className="mb-4 h-[3px] w-12 rounded-full" style={{ background: GOLD }} />
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: GOLD }}>After Assessment</p>
            <h2 className="mt-2 font-heading text-3xl font-bold text-white">Penilaian Selepas Program</h2>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "#C9B2D6" }}>
              Masukkan GF Access Code dan emel yang sama seperti semasa Before Assessment untuk meneruskan.
            </p>
          </motion.div>
          <div className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-semibold" style={{ color: "#E5D4EE" }}>GF Access Code</label>
              <input data-testid="after-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="cth: JTDC-TD26-A01"
                className="mt-2 w-full rounded-2xl border-2 bg-white/5 px-4 py-3.5 text-center text-lg font-bold uppercase tracking-widest text-white outline-none placeholder:text-white/30"
                style={{ borderColor: "rgba(212,175,55,0.4)" }} />
            </div>
            <div>
              <label className="text-sm font-semibold" style={{ color: "#E5D4EE" }}>Emel</label>
              <input data-testid="after-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLookup()}
                placeholder="emel@organisasi.com"
                className="mt-2 w-full rounded-2xl border-2 bg-white/5 px-4 py-3.5 text-white outline-none placeholder:text-white/30"
                style={{ borderColor: "rgba(212,175,55,0.4)" }} />
            </div>
          </div>
          <div className="mt-auto pt-8">
            <button data-testid="after-lookup-btn" onClick={doLookup} disabled={busy || !code.trim() || !email.trim()}
              className="flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
              style={{ background: GOLD, color: PURPLE }}>
              {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Mengesahkan…</> : <>TERUSKAN <ArrowRight className="h-5 w-5" /></>}
            </button>
          </div>
        </div>
      </GFFrame>
    );
  }

  /* ---- Done / Milestone 1 ---- */
  if (stage === "done") {
    const positive = (milestone?.delta ?? 0) >= 0;
    return (
      <GFFrame testId="cohort-after-done-page">
        <div className="flex min-h-screen flex-col px-6 pb-10 pt-10">
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-14 w-14" style={{ color: PURPLE }} />
            <h2 className="mt-4 font-heading text-3xl font-bold" style={{ color: PURPLE }}>Terima kasih!</h2>
            <p className="mt-2 text-sm text-slate-500">Respons After Assessment anda telah direkodkan secara sulit.</p>
          </div>

          {milestone && (
            <div className="mt-8 rounded-2xl p-6 text-white" style={{ background: PURPLE }} data-testid="milestone-card">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>{milestone.title}</p>
              <div className="mt-4 flex items-end gap-5">
                <div>
                  <p className="text-xs" style={{ color: "#9E7EB0" }}>Sebelum</p>
                  <p className="font-heading text-3xl font-bold text-white">{milestone.overall_pre}%</p>
                </div>
                <ArrowRight className="mb-4 h-5 w-5" style={{ color: "#6B4880" }} />
                <div>
                  <p className="text-xs" style={{ color: GOLD }}>Selepas</p>
                  <p className="font-heading text-3xl font-bold" style={{ color: GOLD }}>{milestone.overall_post}%</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl px-3 py-2"
                style={{ background: positive ? "#10B98122" : "#EF444422" }}>
                <TrendingUp className="h-4 w-4" style={{ color: positive ? "#34D399" : "#F87171" }} />
                <span className="text-sm font-bold" style={{ color: positive ? "#34D399" : "#F87171" }}>
                  {milestone.label} ({milestone.delta >= 0 ? "+" : ""}{milestone.delta} mata)
                </span>
              </div>
              <p className="mt-4 text-xs leading-relaxed" style={{ color: "#C9B2D6" }}>
                Skor peribadi ini untuk rujukan anda sahaja. Organisasi hanya menerima dapatan agregat, bukan skor individu.
              </p>
            </div>
          )}

          <button onClick={() => navigate("/")} data-testid="after-done-home"
            className="mt-8 rounded-full py-4 text-sm font-bold text-white" style={{ background: PURPLE }}>
            Selesai
          </button>
        </div>
      </GFFrame>
    );
  }

  /* ---- Quiz ---- */
  if (!data) return <GFFrame><div className="flex min-h-screen items-center justify-center text-slate-400">Memuatkan…</div></GFFrame>;
  const total = data.total;
  const q = data.questions[idx];
  const current = answers[q.id];
  const progress = ((idx + (current ? 1 : 0)) / total) * 100;
  const choose = (v) => {
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    setTimeout(() => { idx < total - 1 ? setIdx(idx + 1) : finish(next); }, 200);
  };

  return (
    <GFFrame testId="cohort-after-quiz-page">
      <div className="flex min-h-screen flex-col">
        <div className="px-6 pt-8">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => (idx > 0 ? setIdx(idx - 1) : setStage("lookup"))} className="text-sm text-slate-400" data-testid="after-q-back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#7A2E9E" }}>After · {data.factor_labels[q.factor]}</span>
            <span className="text-sm font-bold text-slate-500">{idx + 1}/{total}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div className="h-full rounded-full" style={{ background: GOLD }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>
        <div className="flex flex-1 flex-col px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div key={q.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="flex flex-1 flex-col">
              <div className="flex flex-1 items-center">
                <h2 className="font-heading text-2xl font-semibold leading-snug" style={{ color: PURPLE }}>{q.text}</h2>
              </div>
              <div className="space-y-2.5 pb-6">
                {data.scale.map((s) => {
                  const active = current === s.value;
                  return (
                    <button key={s.value} data-testid={`after-scale-${s.value}`} onClick={() => choose(s.value)}
                      className="flex w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.99]"
                      style={active ? { background: PURPLE, borderColor: PURPLE, color: "#fff" } : { background: "#fff", borderColor: "#E4D6EC", color: PURPLE }}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                        style={active ? { background: GOLD, color: PURPLE } : { background: "#F1E9F6", color: "#7A2E9E" }}>{s.value}</span>
                      <span className="text-[15px] font-medium">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        {busy && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: PURPLE }}>
              <RefreshCw className="h-6 w-6 animate-spin" style={{ color: GOLD }} /> Mengira Milestone 1…
            </div>
          </div>
        )}
      </div>
    </GFFrame>
  );
}
